import React, { useEffect, useState } from "react";
import ScrollToTop from "../ScrollToTop";
import AdminSidebar from "../../Component/AdminSidebar";
import {
  deleteDeliveryZone,
  loadDeliveryZones,
  saveDeliveryZone,
} from "../../data/delivery";
import { loadDrivers, saveDriver, deleteDriver } from "../../data/drivers";
import { loadTaxRates, saveTaxRate, deleteTaxRate } from "../../data/taxRates";

const emptyZoneForm = {
  id: "",
  name: "",
  postcode_start: "",
  postcode_end: "",
  fee: "4.90",
  minimum_order: "0",
  minimum_fresh_order: "0",
  minimum_frozen_order: "0",
  free_delivery_threshold: "49",
  supports_fresh: true,
  supports_frozen: false,
  is_active: true,
};

const MyAcconutSetting = () => {
  const [loaderStatus, setLoaderStatus] = useState(true);
  const [zones, setZones] = useState([]);
  const [zoneForm, setZoneForm] = useState(emptyZoneForm);
  const [message, setMessage] = useState("");
  const [activeTab, setActiveTab] = useState("zones");
  const [drivers, setDrivers] = useState([]);
  const [driverForm, setDriverForm] = useState({ nom: "", logo: "", delai: "", position: "" });
  const [editingDriverId, setEditingDriverId] = useState(null);
  const [driverEditForm, setDriverEditForm] = useState({});
  const [driverMessage, setDriverMessage] = useState("");
  const [taxRates, setTaxRates] = useState([]);
  const [taxForm, setTaxForm] = useState({ label: "", rate: "" });
  const [editingTaxId, setEditingTaxId] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const fetchSettings = async () => {
      const [loadedZones, loadedDrivers] = await Promise.all([
        loadDeliveryZones(),
        loadDrivers(),
      ]);
      if (!isMounted) {
        return;
      }
      setZones(loadedZones);
      setDrivers(loadedDrivers);
      const loadedTaxRates = await loadTaxRates();
      setTaxRates(loadedTaxRates);
      setLoaderStatus(false);
    };

    fetchSettings();

    return () => {
      isMounted = false;
    };
  }, []);

  const resetForm = () => {
    setZoneForm(emptyZoneForm);
    setMessage("");
  };

  const refreshSettings = async () => {
    const [loadedZones, loadedDrivers] = await Promise.all([
      loadDeliveryZones(),
      loadDrivers(),
    ]);
    setZones(loadedZones);
    setDrivers(loadedDrivers);
  };

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setZoneForm((current) => ({
      ...current,
      [name]: type === "checkbox" ? checked : value,
    }));
    setMessage("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const saved = await saveDeliveryZone(zoneForm);
    await refreshSettings();
    setZoneForm({
      ...saved,
      fee: String(saved.fee ?? ""),
      minimum_order: String(saved.minimum_order ?? ""),
      minimum_fresh_order: String(saved.minimum_fresh_order ?? ""),
      minimum_frozen_order: String(saved.minimum_frozen_order ?? ""),
      free_delivery_threshold:
        saved.free_delivery_threshold === null
          ? ""
          : String(saved.free_delivery_threshold),
    });
    setMessage("Zone de livraison enregistree.");
  };

  const handleEdit = (zone) => {
    setZoneForm({
      ...zone,
      fee: String(zone.fee ?? ""),
      minimum_order: String(zone.minimum_order ?? ""),
      minimum_fresh_order: String(zone.minimum_fresh_order ?? ""),
      minimum_frozen_order: String(zone.minimum_frozen_order ?? ""),
      free_delivery_threshold:
        zone.free_delivery_threshold === null
          ? ""
          : String(zone.free_delivery_threshold),
    });
    setMessage("");
  };

  const handleDelete = async (id) => {
    await deleteDeliveryZone(id);
    await refreshSettings();
    if (zoneForm.id === id) {
      resetForm();
    }
    setMessage("Zone supprimee.");
  };

  return (
    <div>
      <ScrollToTop />
      <section className="admin-section">
        <div className="container">
          <div className="row">
            <AdminSidebar activePath="/MyAccountSetting" />

            <div className="col-lg-9 col-md-8 col-12">
              {loaderStatus ? (
                <div className="p-6 p-lg-10">
                  <div className="alert alert-light border mb-0">
                    Chargement des parametres...
                  </div>
                </div>
              ) : (
                <div className="p-6 p-lg-10">
                  <div className="mb-6">
                    <h2 className="mb-1">Livraison</h2>
                    <p className="text-muted mb-0">
                      Configurez les zones de livraison et les transporteurs
                      (livreurs) proposes au checkout.
                    </p>
                  </div>

                  <div className="admin-tabs">
                    {[
                      { key: "zones", label: "Zones" },
                      { key: "drivers", label: "Livreurs" },
                      { key: "taxes", label: "TVA" },
                    ].map((tab) => (
                      <button
                        key={tab.key}
                        type="button"
                        className={`admin-tab-btn${activeTab === tab.key ? " active" : ""}`}
                        onClick={() => setActiveTab(tab.key)}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>

                  {activeTab === "zones" && (
                    <div className="row g-4">
                    <div className="col-lg-5">
                      <div className="card border-0 shadow-sm h-100">
                        <div className="card-body p-4">
                          <div className="d-flex justify-content-between align-items-center mb-4">
                            <h5 className="mb-0">
                              {zoneForm.id ? "Modifier la zone" : "Nouvelle zone"}
                            </h5>
                            <button
                              type="button"
                              className="btn btn-outline-dark btn-sm"
                              onClick={resetForm}
                            >
                              Reinitialiser
                            </button>
                          </div>
                          <form onSubmit={handleSubmit}>
                            <div className="row g-3">
                              <div className="col-12">
                                <label className="form-label">Nom</label>
                                <input
                                  name="name"
                                  className="form-control"
                                  value={zoneForm.name}
                                  onChange={handleChange}
                                  required
                                />
                              </div>
                              <div className="col-md-6">
                                <label className="form-label">Code debut</label>
                                <input
                                  name="postcode_start"
                                  className="form-control"
                                  value={zoneForm.postcode_start}
                                  onChange={handleChange}
                                  required
                                />
                              </div>
                              <div className="col-md-6">
                                <label className="form-label">Code fin</label>
                                <input
                                  name="postcode_end"
                                  className="form-control"
                                  value={zoneForm.postcode_end}
                                  onChange={handleChange}
                                  required
                                />
                              </div>
                              <div className="col-md-4">
                                <label className="form-label">Frais</label>
                                <input
                                  name="fee"
                                  type="number"
                                  step="0.01"
                                  className="form-control"
                                  value={zoneForm.fee}
                                  onChange={handleChange}
                                />
                              </div>
                              <div className="col-md-4">
                                <label className="form-label">Minimum commande</label>
                                <input
                                  name="minimum_order"
                                  type="number"
                                  step="0.01"
                                  className="form-control"
                                  value={zoneForm.minimum_order}
                                  onChange={handleChange}
                                />
                              </div>
                              <div className="col-md-4">
                                <label className="form-label">Seuil gratuit</label>
                                <input
                                  name="free_delivery_threshold"
                                  type="number"
                                  step="0.01"
                                  className="form-control"
                                  value={zoneForm.free_delivery_threshold}
                                  onChange={handleChange}
                                />
                              </div>
                              <div className="col-md-6">
                                <label className="form-label">Minimum frais</label>
                                <input
                                  name="minimum_fresh_order"
                                  type="number"
                                  step="0.01"
                                  className="form-control"
                                  value={zoneForm.minimum_fresh_order}
                                  onChange={handleChange}
                                />
                              </div>
                              <div className="col-md-6">
                                <label className="form-label">Minimum surgeles</label>
                                <input
                                  name="minimum_frozen_order"
                                  type="number"
                                  step="0.01"
                                  className="form-control"
                                  value={zoneForm.minimum_frozen_order}
                                  onChange={handleChange}
                                />
                              </div>
                              <div className="col-12">
                                <div className="form-check mb-2">
                                  <input
                                    id="supportsFresh"
                                    name="supports_fresh"
                                    type="checkbox"
                                    className="form-check-input"
                                    checked={zoneForm.supports_fresh}
                                    onChange={handleChange}
                                  />
                                  <label className="form-check-label" htmlFor="supportsFresh">
                                    Accepte les produits frais
                                  </label>
                                </div>
                                <div className="form-check mb-2">
                                  <input
                                    id="supportsFrozen"
                                    name="supports_frozen"
                                    type="checkbox"
                                    className="form-check-input"
                                    checked={zoneForm.supports_frozen}
                                    onChange={handleChange}
                                  />
                                  <label className="form-check-label" htmlFor="supportsFrozen">
                                    Accepte les produits surgeles
                                  </label>
                                </div>
                                <div className="form-check">
                                  <input
                                    id="zoneActive"
                                    name="is_active"
                                    type="checkbox"
                                    className="form-check-input"
                                    checked={zoneForm.is_active}
                                    onChange={handleChange}
                                  />
                                  <label className="form-check-label" htmlFor="zoneActive">
                                    Zone active
                                  </label>
                                </div>
                              </div>
                              {message ? (
                                <div className="col-12">
                                  <div className="alert alert-success mb-0">{message}</div>
                                </div>
                              ) : null}
                              <div className="col-12">
                                <button className="btn btn-primary" type="submit">
                                  {zoneForm.id ? "Mettre a jour" : "Ajouter"}
                                </button>
                              </div>
                            </div>
                          </form>
                        </div>
                      </div>
                    </div>

                    <div className="col-lg-7">
                      <div className="card border-0 shadow-sm h-100">
                        <div className="card-body p-4">
                          <h5 className="mb-4">Zones configurees</h5>
                          <div className="table-responsive">
                            <table className="table mb-0 align-middle">
                              <thead className="table-light">
                                <tr>
                                  <th>Zone</th>
                                  <th>Codes postaux</th>
                                  <th>Frais</th>
                                  <th>Conditions</th>
                                  <th>Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {zones.map((zone) => (
                                  <tr key={zone.id}>
                                    <td>
                                      <div className="fw-semibold">{zone.name}</div>
                                      <small className="text-muted">
                                        {zone.is_active ? "Active" : "Inactive"}
                                      </small>
                                    </td>
                                    <td>
                                      {zone.postcode_start} - {zone.postcode_end}
                                    </td>
                                    <td>{Number(zone.fee || 0).toFixed(2)} EUR</td>
                                    <td>
                                      <small className="d-block text-muted">
                                        Min: {Number(zone.minimum_order || 0).toFixed(2)} EUR
                                      </small>
                                      <small className="d-block text-muted">
                                        Frais min: {Number(zone.minimum_fresh_order || 0).toFixed(2)} EUR
                                      </small>
                                      <small className="d-block text-muted">
                                        Surgeles min: {Number(zone.minimum_frozen_order || 0).toFixed(2)} EUR
                                      </small>
                                      <small className="d-block text-muted">
                                        Gratuit:{" "}
                                        {zone.free_delivery_threshold === null
                                          ? "-"
                                          : `${Number(zone.free_delivery_threshold).toFixed(2)} EUR`}
                                      </small>
                                      <small className="d-block text-muted">
                                        Frais: {zone.supports_fresh ? "oui" : "non"} /
                                        Surgeles: {zone.supports_frozen ? "oui" : "non"}
                                      </small>
                                    </td>
                                    <td>
                                      <div className="d-flex gap-2 flex-wrap">
                                        <button
                                          type="button"
                                          className="btn btn-outline-dark btn-sm"
                                          onClick={() => handleEdit(zone)}
                                        >
                                          Modifier
                                        </button>
                                        <button
                                          type="button"
                                          className="btn btn-outline-danger btn-sm"
                                          onClick={() => handleDelete(zone.id)}
                                        >
                                          Supprimer
                                        </button>
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                                {!zones.length ? (
                                  <tr>
                                    <td colSpan="5" className="text-muted">
                                      Aucune zone de livraison configuree.
                                    </td>
                                  </tr>
                                ) : null}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  )}


                  {activeTab === "drivers" && (
                    <>
                    <div className="card border-0 shadow-sm mt-4">
                      <div className="card-body p-4">
                        <h5 className="mb-4">Nouveau livreur</h5>
                        <form
                          onSubmit={async (e) => {
                            e.preventDefault();
                            if (!driverForm.nom.trim()) return;
                            await saveDriver({
                              nom: driverForm.nom.trim(),
                              logo: driverForm.logo.trim(),
                              delai: driverForm.delai.trim(),
                              position: Number(driverForm.position) || 0,
                              phone: "",
                              isActive: true,
                            });
                            setDriverForm({ nom: "", logo: "", delai: "", position: "" });
                            setDriverMessage("Livreur ajoute.");
                            const updated = await loadDrivers();
                            setDrivers(updated);
                          }}
                        >
                          <div className="row g-3">
                            <div className="col-lg-3 col-md-6">
                              <label className="form-label">Nom (societe)</label>
                              <input
                                className="form-control"
                                placeholder="ex: Chronopost"
                                value={driverForm.nom}
                                onChange={(e) => {
                                  setDriverForm((c) => ({ ...c, nom: e.target.value }));
                                  setDriverMessage("");
                                }}
                                required
                              />
                            </div>
                            <div className="col-lg-3 col-md-6">
                              <label className="form-label">Logo (URL)</label>
                              <input
                                className="form-control"
                                placeholder="https://..."
                                value={driverForm.logo}
                                onChange={(e) => {
                                  setDriverForm((c) => ({ ...c, logo: e.target.value }));
                                  setDriverMessage("");
                                }}
                              />
                            </div>
                            <div className="col-lg-4 col-md-6">
                              <label className="form-label">Delai</label>
                              <input
                                className="form-control"
                                placeholder="ex: Colis livre le lendemain avant 13h"
                                value={driverForm.delai}
                                onChange={(e) => {
                                  setDriverForm((c) => ({ ...c, delai: e.target.value }));
                                  setDriverMessage("");
                                }}
                              />
                            </div>
                            <div className="col-lg-2 col-md-6">
                              <label className="form-label">Position (1 = premier)</label>
                              <input
                                className="form-control"
                                type="number"
                                value={driverForm.position}
                                onChange={(e) => {
                                  setDriverForm((c) => ({ ...c, position: e.target.value }));
                                  setDriverMessage("");
                                }}
                              />
                            </div>
                            <div className="col-12 d-flex align-items-end gap-2">
                              <button className="btn btn-primary" type="submit">
                                Ajouter
                              </button>
                              {driverMessage ? (
                                <div className="alert alert-success mb-0">{driverMessage}</div>
                              ) : null}
                            </div>
                          </div>
                        </form>
                      </div>
                    </div>

                    <div className="card border-0 shadow-sm mt-4">
                      <div className="card-body p-4">
                        <h5 className="mb-4">Livreurs</h5>
                        <div className="table-responsive">
                          <table className="table mb-0 align-middle">
                            <thead className="table-light">
                              <tr>
                                <th style={{ width: 60 }}>ID</th>
                                <th style={{ width: 160 }}>Nom</th>
                                <th style={{ width: 100 }}>Logo</th>
                                <th>Delai</th>
                                <th style={{ width: 80 }}>Position</th>
                                <th style={{ width: 220 }}>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {[...drivers]
                                .sort((a, b) => (Number(a.position) || 0) - (Number(b.position) || 0))
                                .map((driver) => (
                                <tr key={driver.id}>
                                  <td className="text-muted">{driver.id}</td>
                                  <td>
                                    {editingDriverId === driver.id ? (
                                      <input
                                        className="form-control form-control-sm"
                                        value={driverEditForm.nom || ""}
                                        onChange={(e) =>
                                          setDriverEditForm((c) => ({ ...c, nom: e.target.value }))
                                        }
                                      />
                                    ) : (
                                      <div className="fw-semibold">{driver.nom}</div>
                                    )}
                                  </td>
                                  <td>
                                    {editingDriverId === driver.id ? (
                                      <input
                                        className="form-control form-control-sm"
                                        value={driverEditForm.logo || ""}
                                        onChange={(e) =>
                                          setDriverEditForm((c) => ({ ...c, logo: e.target.value }))
                                        }
                                      />
                                    ) : driver.logo ? (
                                      <img
                                        src={driver.logo}
                                        alt={driver.nom}
                                        className="rounded"
                                        style={{ width: 72, height: 36, objectFit: "contain" }}
                                      />
                                    ) : (
                                      <span className="text-muted small">-</span>
                                    )}
                                  </td>
                                  <td>
                                    {editingDriverId === driver.id ? (
                                      <input
                                        className="form-control form-control-sm"
                                        value={driverEditForm.delai || ""}
                                        onChange={(e) =>
                                          setDriverEditForm((c) => ({ ...c, delai: e.target.value }))
                                        }
                                      />
                                    ) : (
                                      driver.delai || "-"
                                    )}
                                  </td>
                                  <td>
                                    {editingDriverId === driver.id ? (
                                      <input
                                        className="form-control form-control-sm"
                                        type="number"
                                        value={driverEditForm.position}
                                        onChange={(e) =>
                                          setDriverEditForm((c) => ({ ...c, position: e.target.value }))
                                        }
                                      />
                                    ) : (
                                      driver.position
                                    )}
                                  </td>
                                  <td>
                                    <div className="d-flex gap-2 flex-wrap">
                                      {editingDriverId === driver.id ? (
                                        <button
                                          type="button"
                                          className="btn btn-outline-success btn-sm"
                                          onClick={async () => {
                                            await saveDriver({
                                              ...driver,
                                              nom: driverEditForm.nom ?? driver.nom,
                                              logo: driverEditForm.logo ?? driver.logo,
                                              delai: driverEditForm.delai ?? driver.delai,
                                              position: Number(driverEditForm.position ?? driver.position),
                                            });
                                            setEditingDriverId(null);
                                            setDriverEditForm({});
                                            const updated = await loadDrivers();
                                            setDrivers(updated);
                                          }}
                                        >
                                          Sauver
                                        </button>
                                      ) : (
                                        <button
                                          type="button"
                                          className="btn btn-outline-dark btn-sm"
                                          onClick={() => {
                                            setEditingDriverId(driver.id);
                                            setDriverEditForm({
                                              nom: driver.nom,
                                              logo: driver.logo,
                                              delai: driver.delai,
                                              position: driver.position,
                                            });
                                          }}
                                        >
                                          Modifier
                                        </button>
                                      )}
                                      <button
                                        type="button"
                                        className="btn btn-outline-danger btn-sm"
                                        onClick={async () => {
                                          if (!window.confirm("Supprimer ce livreur ?")) return;
                                          await deleteDriver(driver.id);
                                          const updated = await loadDrivers();
                                          setDrivers(updated);
                                        }}
                                      >
                                        Supprimer
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                              {!drivers.length ? (
                                <tr>
                                  <td colSpan="6" className="text-muted">
                                    Aucun livreur configure.
                                  </td>
                                </tr>
                              ) : null}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                    </>
                  )}

                  {activeTab === "taxes" && (
                    <div className="row g-4">
                      <div className="col-lg-5">
                        <div className="card border-0 shadow-sm h-100">
                          <div className="card-body p-4">
                            <h5 className="mb-3">{editingTaxId ? "Modifier le taux" : "Ajouter un taux TVA"}</h5>
                            <div className="mb-3">
                              <label className="form-label">Libelle</label>
                              <input
                                className="form-control"
                                placeholder="ex: TVA 5.5%"
                                value={taxForm.label}
                                onChange={(e) => setTaxForm({ ...taxForm, label: e.target.value })}
                              />
                            </div>
                            <div className="mb-3">
                              <label className="form-label">Taux (%)</label>
                              <input
                                type="number"
                                step="0.1"
                                className="form-control"
                                placeholder="ex: 5.5"
                                value={taxForm.rate}
                                onChange={(e) => setTaxForm({ ...taxForm, rate: e.target.value })}
                              />
                            </div>
                            <div className="d-flex gap-2">
                              <button
                                className="btn btn-dark"
                                onClick={async () => {
                                  if (!taxForm.label || !taxForm.rate) return;
                                  await saveTaxRate({
                                    id: editingTaxId || undefined,
                                    label: taxForm.label,
                                    rate: Number(taxForm.rate),
                                  });
                                  setTaxForm({ label: "", rate: "" });
                                  setEditingTaxId(null);
                                  setTaxRates(await loadTaxRates());
                                }}
                              >
                                {editingTaxId ? "Mettre a jour" : "Ajouter"}
                              </button>
                              {editingTaxId && (
                                <button
                                  className="btn btn-outline-secondary"
                                  onClick={() => { setEditingTaxId(null); setTaxForm({ label: "", rate: "" }); }}
                                >
                                  Annuler
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="col-lg-7">
                        <div className="card border-0 shadow-sm h-100">
                          <div className="card-body p-4">
                            <h5 className="mb-3">Taux TVA configures</h5>
                            <div className="table-responsive">
                              <table className="table table-sm mb-0">
                                <thead>
                                  <tr>
                                    <th>Libelle</th>
                                    <th className="text-end">Taux</th>
                                    <th className="text-center">Actif</th>
                                    <th className="text-end">Actions</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {taxRates.map((t) => (
                                    <tr key={t.id}>
                                      <td className="fw-semibold">{t.label}</td>
                                      <td className="text-end">{t.rate}%</td>
                                      <td className="text-center">
                                        <span className={`badge ${t.is_active ? "bg-success" : "bg-secondary"}`}>
                                          {t.is_active ? "Oui" : "Non"}
                                        </span>
                                      </td>
                                      <td className="text-end">
                                        <button
                                          className="btn btn-sm btn-outline-dark me-1"
                                          onClick={() => {
                                            setEditingTaxId(t.id);
                                            setTaxForm({ label: t.label, rate: String(t.rate) });
                                          }}
                                        >
                                          <i className="fas fa-pen" />
                                        </button>
                                        <button
                                          className="btn btn-sm btn-outline-danger"
                                          onClick={async () => {
                                            await deleteTaxRate(t.id);
                                            setTaxRates(await loadTaxRates());
                                          }}
                                        >
                                          <i className="fas fa-trash" />
                                        </button>
                                      </td>
                                    </tr>
                                  ))}
                                  {taxRates.length === 0 && (
                                    <tr>
                                      <td colSpan={4} className="text-center text-muted py-3">
                                        Aucun taux configure.
                                      </td>
                                    </tr>
                                  )}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MyAcconutSetting;
